import type { PrepareSendMessagesRequest } from 'ai';

import type { AiContext } from './ai-context';
import type { SidebarChatMessage } from './sidebar-chat-message';

import { getSerializedByteLength, SIDEBAR_CHAT_REQUEST_TARGET_BYTES } from '../chat-context-size';

export type SidebarChatDiffContextMode = 'complete' | 'focused_file_only' | 'manifest_only';

export type SidebarChatRequestBody = {
  id?: string;
  context?: AiContext;
  contextMode?: SidebarChatDiffContextMode;
  installationId?: number | null;
  pullRequestId?: string | null;
  model?: string;
};

type SidebarChatRequestEnvelope = {
  id?: string;
  messageId?: string;
  messages?: SidebarChatMessage[];
  trigger?: 'submit-message' | 'regenerate-message';
};

function omitDuplicateFiles(context: AiContext): AiContext {
  const result = { ...context };
  delete result.files;
  return result;
}

function prepareDiffContext(context: AiContext, mode: SidebarChatDiffContextMode): AiContext {
  const withoutDuplicateFiles = omitDuplicateFiles(context);
  if (mode === 'complete') {
    return withoutDuplicateFiles;
  }

  const focusedFilename = context.visibleFilename ?? context.activeFilename;
  return {
    ...withoutDuplicateFiles,
    codeContent: withoutDuplicateFiles.codeContent?.map((file) => ({
      ...file,
      oldContent: undefined,
      newContent: undefined,
      patch:
        mode === 'focused_file_only' && file.filename === focusedFilename ? file.patch : undefined,
    })),
  };
}

/**
 * Shape and measure the complete JSON body that will be sent to the chat endpoint. This keeps
 * payload policy at the actual transport boundary, after message history and per-send overrides
 * are known. The file manifest is always retained; only diff bodies are progressively omitted.
 */
export function prepareSidebarChatRequestBody(params: {
  body: SidebarChatRequestBody;
  envelope?: SidebarChatRequestEnvelope;
}) {
  const envelope = params.envelope ?? {};
  const focusedFilename =
    params.body.context?.visibleFilename ?? params.body.context?.activeFilename;
  const hasFocusedPatch = params.body.context?.codeContent?.some(
    (file) => file.filename === focusedFilename && Boolean(file.patch),
  );
  const modes: SidebarChatDiffContextMode[] = hasFocusedPatch
    ? ['complete', 'focused_file_only', 'manifest_only']
    : ['complete', 'manifest_only'];

  for (const mode of modes) {
    const prepared = {
      ...params.body,
      ...envelope,
      context: params.body.context ? prepareDiffContext(params.body.context, mode) : undefined,
      contextMode: mode,
    };
    if (getSerializedByteLength(prepared) <= SIDEBAR_CHAT_REQUEST_TARGET_BYTES) {
      return prepared;
    }
  }

  throw new Error(
    '413 FUNCTION_PAYLOAD_TOO_LARGE: Chat history and pull request metadata exceed the safe request limit.',
  );
}

export const prepareSidebarChatSendMessagesRequest: PrepareSendMessagesRequest<
  SidebarChatMessage
> = ({ id, messages, body, trigger, messageId }) => ({
  body: prepareSidebarChatRequestBody({
    body: (body ?? {}) as SidebarChatRequestBody,
    envelope: { id, messages, trigger, messageId },
  }),
});

export function buildSidebarChatTransportBody(params: {
  id: string;
  context: AiContext;
  installationId: number | null;
  pullRequestId: string | null;
}): SidebarChatRequestBody {
  return {
    id: params.id,
    context: params.context,
    installationId: params.installationId,
    pullRequestId: params.pullRequestId,
  };
}

export function buildSidebarChatModelBody(model: string): SidebarChatRequestBody {
  return { model };
}
